import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  RefreshCw,
  Settings,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY = {
  error: {
    label: "Error",
    icon: AlertCircle,
    color: "text-red-300",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    badge: "bg-red-500/20 text-red-300",
  },
  warn: {
    label: "Aviso",
    icon: AlertTriangle,
    color: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    badge: "bg-amber-500/20 text-amber-200",
  },
  info: {
    label: "Info",
    icon: Info,
    color: "text-blue-300",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    badge: "bg-blue-500/20 text-blue-200",
  },
};

const IssueRow = ({ issue }) => {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY[issue.severity] || SEVERITY.info;
  const Icon = sev.icon;
  return (
    <div
      className={`${sev.bg} border ${sev.border} rounded-lg overflow-hidden`}
      data-testid={`diag-issue-${issue.code}-${issue.creatorUsername}-${issue.tier}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-white/5 transition-colors"
      >
        <Icon className={`w-5 h-5 ${sev.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-white font-semibold text-sm">
              @{issue.creatorUsername}
            </span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                issue.tier === "vip"
                  ? "bg-amber-500/20 text-amber-200"
                  : "bg-blue-500/20 text-blue-200"
              }`}
            >
              {issue.tier.toUpperCase()}
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sev.badge}`}>
              {sev.label}
            </span>
          </div>
          <p className={`${sev.color} font-medium text-sm`}>{issue.title}</p>
          <p className="text-white/60 text-xs mt-0.5 line-clamp-1">{issue.message}</p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/40 flex-shrink-0 mt-1" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-white/5">
          {issue.message && (
            <div>
              <p className="text-[10px] text-white/40 uppercase font-semibold mb-1">
                Detalle
              </p>
              <p className="text-white/80 text-xs">{issue.message}</p>
            </div>
          )}
          {issue.raw && (
            <div>
              <p className="text-[10px] text-white/40 uppercase font-semibold mb-1">
                Mensaje técnico
              </p>
              <code className="text-white/60 text-[11px] font-mono block bg-black/30 p-2 rounded">
                {issue.raw}
              </code>
            </div>
          )}
          {issue.fix && (
            <div>
              <p className="text-[10px] text-white/40 uppercase font-semibold mb-1">
                Cómo arreglarlo
              </p>
              <p className="text-white/90 text-xs">{issue.fix}</p>
            </div>
          )}
          {issue.channelId && (
            <p className="text-[10px] text-white/40 font-mono truncate">
              Canal: {issue.channelId}
              {issue.title && issue.memberCount != null && (
                <> · {issue.memberCount} miembros</>
              )}
            </p>
          )}
          <Link
            to={`/admin/creadoras/${issue.creatorUsername}/auto-posting`}
            className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white bg-white/10 hover:bg-white/15 px-3 py-1.5 rounded-lg mt-1"
          >
            <Settings className="w-3 h-3" />
            Abrir panel de la modelo
          </Link>
        </div>
      )}
    </div>
  );
};

const FilterTab = ({ active, onClick, children, count, color = "white" }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
      active
        ? "bg-white text-black"
        : `bg-white/5 text-white/60 hover:text-white hover:bg-white/10`
    }`}
  >
    {children}
    {count != null && (
      <span
        className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
          active ? "bg-black/20" : `bg-${color}/20`
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const AdminAutoPostDiagnostics = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ issues: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | error | warn | info

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/autopost/diagnostics`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error(
          "Diagnóstico no disponible en producción todavía. Haz Deploy en Emergent.",
          { duration: 8000 }
        );
        setData({ issues: [], totals: {} });
        return;
      }
      const d = await r.json();
      setData(d);
      const errs = d.totals?.errors || 0;
      if (errs === 0 && (d.totals?.warnings || 0) === 0) {
        toast.success("✅ Sin problemas detectados");
      }
    } catch (e) {
      toast.error("Error al ejecutar diagnóstico");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered =
    filter === "all"
      ? data.issues
      : data.issues.filter((i) => i.severity === filter);

  const totals = data.totals || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        <Link
          to="/admin/auto-posting"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Canales
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-2">
              🔍 Diagnóstico
            </h1>
            <p className="text-white/60 text-sm">
              Revisa cada canal y descubre exactamente qué está mal configurado
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="diag-rerun"
            className="px-3 py-2 bg-white text-black text-xs font-bold rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Analizando..." : "Reejecutar"}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-white/60 text-xs">Canales</div>
            <div className="text-white text-2xl font-bold">{totals.channels || 0}</div>
          </div>
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
            <div className="text-green-300 text-xs">Sin problemas</div>
            <div className="text-white text-2xl font-bold flex items-center gap-1">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              {totals.ok || 0}
            </div>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <div className="text-red-300 text-xs">Errores</div>
            <div className="text-white text-2xl font-bold">{totals.errors || 0}</div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <div className="text-amber-300 text-xs">Avisos</div>
            <div className="text-white text-2xl font-bold">{totals.warnings || 0}</div>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
            <div className="text-blue-300 text-xs">Info</div>
            <div className="text-white text-2xl font-bold">{totals.info || 0}</div>
          </div>
        </div>

        {/* Filter tabs */}
        {data.issues.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <FilterTab
              active={filter === "all"}
              onClick={() => setFilter("all")}
              count={data.issues.length}
            >
              Todos
            </FilterTab>
            <FilterTab
              active={filter === "error"}
              onClick={() => setFilter("error")}
              count={totals.errors}
              color="red-500"
            >
              ❌ Errores
            </FilterTab>
            <FilterTab
              active={filter === "warn"}
              onClick={() => setFilter("warn")}
              count={totals.warnings}
              color="amber-500"
            >
              ⚠️ Avisos
            </FilterTab>
            <FilterTab
              active={filter === "info"}
              onClick={() => setFilter("info")}
              count={totals.info}
              color="blue-500"
            >
              ℹ️ Info
            </FilterTab>
          </div>
        )}

        {/* Issues */}
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-3">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            <p className="text-white/60 text-sm">
              Verificando canales con Telegram... (puede tardar unos segundos)
            </p>
          </div>
        ) : data.issues.length === 0 ? (
          <div className="py-16 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-white text-lg font-semibold">
              ✨ Todo está perfecto
            </p>
            <p className="text-white/60 text-sm mt-1">
              Los {totals.channels || 0} canales están bien configurados.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-8">
            Sin issues en este filtro
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((issue, i) => (
              <IssueRow
                key={`${issue.creatorUsername}-${issue.tier}-${issue.code}-${i}`}
                issue={issue}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAutoPostDiagnostics;
